import PulseLoading from "@/design-system/pulse-loading";
import type { ITelemetryCustom } from "@/lib/telemetry";
import type { LikedType } from "@/src/contexts";
import type { IComponent } from "@Components/Component";
import { defaultAttributes } from "@Components/Component";
import type { IImageLoader } from "@Components/ImageLoader";
import ImageLoader from "@Components/ImageLoader";
import type { TelemetryProps } from "@Components/Modules/Helpers";
import { requestTrack } from "@Components/Modules/Helpers";
import TelemetryHoc from "@Components/telemetry-hoc/telemetry-hoc";
import * as stylex from "@stylexjs/stylex";

import type { OmitStyles, QueryXsMax } from "../../styles/tokens.stylex";
import { ComposableSpacing, colors } from "../../styles/tokens.stylex";
import type { ISpecialCashback } from "../cashback/CashbackCommonInterfaces";
import CashbackLabel from "../cashback/CashbackLabel";
import { TransitionLinkV2 } from "./TransitionLinkV2";
import FavoriteLink from "./links/FavoriteLink";

import * as css from "./LogoTile.module.css";

const xsMax: QueryXsMax = "@media (max-width: 759px)";

export interface ILogoTile extends OmitStyles<IComponent>, TelemetryProps {
    id: string;
    logo: IImageLoader;
    brandName?: string;
    href: string;
    trackingActions?: string;
    newFlyerText?: string;
    label?: string;
    specialCashback?: ISpecialCashback;
    onscreenTrack?: ITelemetryCustom;
    onClick?: React.MouseEventHandler<HTMLAnchorElement>;
    type?: LikedType;
}

export default function LogoTile<TProps extends ILogoTile>({ styleX, ...props }: TProps) {
    const renderImage = () => {
        if (!props.logo) {
            return <PulseLoading />;
        }
        const image = <ImageLoader {...props.logo} size={300} srcSetAxis="width" />;

        if (props.href === null) {
            return image;
        }

        return (
            <TransitionLinkV2
                id={props.id}
                href={props.href}
                onClick={(ev) => {
                    if (props.onClick) {
                        props.onClick(ev);
                    }
                    requestTrack(props.trackingActions);
                }}
                styleX={styles.logoOverride}
            >
                {image}
            </TransitionLinkV2>
        );
    };

    return (
        <div
            {...defaultAttributes(
                props,
                css.logoTileComponent,
                stylex.props(styles.logoTile, styleX).className,
            )}
        >
            {props.newFlyerText && <div {...stylex.props(styles.flyersText)}>{props.newFlyerText}</div>}
            <TelemetryHoc
                telemetryCustom={props.onscreenTrack}
                styleX={[styles.logo, props.type && styles.logoLink]}
            >
                {renderImage()}
            </TelemetryHoc>
            {props.type && (
                <FavoriteLink
                    {...stylex.props(styles.favoriteLink)}
                    id={props.id}
                    type={props.type}
                    favoriteLinkIconSize="border"
                    showText
                />
            )}
            {props.label && (
                <CashbackLabel
                    styleX={styles.cashbackLabel}
                    specialCashback={props.specialCashback}
                    value={props.label}
                />
            )}
        </div>
    );
}

const styles = stylex.create({
    logoTile: {
        padding: {
            default: "0 15px",
            [xsMax]: "0 10px",
        },
        borderRadius: ComposableSpacing.s1,
        outline: `1px solid ${colors.b_centas}`,
        backgroundColor: colors.b_Emphasis,
        position: "relative",
        textAlign: "center",
    },
    logo: {
        padding: { default: "15px 0", [xsMax]: "10px 0" },
        overflow: "hidden",
        height: "100%",
        width: "100%",
    },
    logoLink: {
        borderBottomColor: colors.b_centas,
        borderBottomStyle: "solid",
        borderBottomWidth: "1px",
        height: "74%",
    },
    logoOverride: {
        paddingBlock: "0",
        paddingInline: "10px",
        display: "flex",
        height: "100%",
    },
    flyersText: {
        backgroundColor: "#00cb53",

        color: colors.b_Emphasis,
        position: "absolute",
        zIndex: 1,
        borderBottomLeftRadius: "0",
        borderBottomRightRadius: "0",
        borderTopLeftRadius: "5px",
        borderTopRightRadius: "5px",
        left: 0,
        right: 0,
    },
    favoriteLink: {
        height: "26%",
        marginBottom: 0,
        marginLeft: "auto",
        marginRight: "auto",
        marginTop: 0,
    },
    cashbackLabel: {
        position: "absolute",
        left: "7px",
        paddingRight: "7px",
        top: "7px",
    },
});
